<template>
  <div class="sp-wrapper">
    <div class="separate-products-container elevation-3">
      <!-- Delivery Strip -->
      <div class="delivery-strip">
        <!-- Note block -->
        <div class="strip-block strip-block--note">
          <span class="strip-block-label">ใบนำเข้า · DELIVERY NOTE</span>
          <div
            class="d-flex align-center"
            style="gap: 8px;">
            <span class="strip-code">{{ note.code }}</span>
            <span
              v-if="note.status"
              class="strip-status">
              <v-icon
                x-small
                color="#2e7d4f"
                style="margin-right: 2px;">mdi-check-circle</v-icon>
              {{ note.status }}
            </span>
          </div>
        </div>

        <div class="strip-divider" />

        <!-- Products -->
        <div class="strip-block">
          <span class="strip-block-label">สินค้า · PRODUCTS</span>
          <span class="strip-block-value">
            {{ total.product | showNumberFormat() }}<span class="strip-block-unit"> แบบ</span>
          </span>
        </div>

        <div class="strip-divider" />

        <!-- Total Qty -->
        <div class="strip-block">
          <span class="strip-block-label">จำนวน · TOTAL QTY</span>
          <span class="strip-block-value">
            {{ total.amount | showNumberFormat() }}<span class="strip-block-unit"> ชิ้น</span>
          </span>
        </div>

        <div class="strip-divider" />

        <!-- Allocated -->
        <div class="strip-block">
          <span class="strip-block-label">ALLOCATED</span>
          <span class="strip-block-value">
            {{ totalAllocated }}<span class="strip-block-unit"> /{{ totalQty }}</span>
          </span>
        </div>

        <div class="strip-divider" />

        <!-- % Sum -->
        <div class="strip-block">
          <span class="strip-block-label">% รวม · SUM</span>
          <span
            class="strip-block-value"
            :class="totalQty > 0 && totalAllocated === totalQty ? 'text-ok' : 'text-warn'">
            {{ totalPercent | showFullPriceFormat() }}%
          </span>
        </div>

        <div style="flex: 1;" />

        <!-- Action buttons -->
        <div
          class="d-flex align-center"
          style="gap: 8px;">
          <v-btn
            elevation="0"
            tile
            small
            class="strip-btn"
            @click="mappingSkus(true)">
            <v-icon
              small
              color="#214dff"
              class="mr-1">
              mdi-creation
            </v-icon>
            กระจายสินค้า
          </v-btn>
          <v-btn
            elevation="0"
            tile
            small
            class="strip-btn"
            @click="exportDataToCSV()">
            <v-icon
              small
              class="mr-1">
              mdi-download
            </v-icon>
            Export
          </v-btn>
          <v-btn
            elevation="0"
            tile
            small
            class="strip-btn strip-btn--primary"
            @click="confirmMoveProducts()">
            <v-icon
              small
              color="#ffffff"
              class="mr-1">
              mdi-transfer-right
            </v-icon>
            ย้ายสินค้า
          </v-btn>
        </div>
      </div>

      <!-- Table Wrapper -->
      <div class="table-outer">
        <table class="allocation-table">
          <thead>
            <tr>
              <!-- Expand chevron col -->
              <th class="col-expand sticky-left-0" />
              <!-- Product col -->
              <th class="col-product sticky-left-36 text-left">
                <span class="col-label-th">สินค้า</span>
                <span class="col-label-en"> · Product</span>
              </th>
              <!-- Qty col -->
              <th class="col-qty text-right">
                <span class="col-label-th">จำนวน</span>
                <span class="col-label-en"> · Qty</span>
              </th>
              <!-- Allocation col -->
              <th class="col-alloc text-center">
                Allocation
              </th>
              <!-- Branch cols -->
              <th
                v-for="branch in selectedBranches"
                :key="branch.id"
                class="col-branch">
                <div class="branch-header">
                  <span
                    class="branch-name"
                    :title="branch.name">
                    {{ branch.name }}
                  </span>
                  <div class="branch-pct-row">
                    <input
                      v-model.number="branch.percent"
                      type="number"
                      min="0"
                      max="100"
                      class="branch-pct-field" />
                    <span class="branch-pct-unit">%</span>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody
            v-for="({ product, item, pi, si }) in flatSkus"
            :key="`group-${pi}-${si}`">
            <!-- Main row -->
            <tr
              class="sku-row"
              :class="{ 'row-locked': lockedRows[rowKey(pi, si)], 'row-error': item.isError }">
              <!-- Expand toggle -->
              <td class="col-expand sticky-left-0 text-center">
                <v-btn
                  icon
                  x-small
                  @click="toggleExpand(rowKey(pi, si))">
                  <v-icon small>
                    {{ expandedRows[rowKey(pi, si)] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                </v-btn>
              </td>
              <!-- Product info -->
              <td class="col-product sticky-left-36">
                <div class="product-cell">
                  <img
                    v-if="product.photoUrl"
                    :src="$convertImgix(`${product.photoUrl}?w=80`)"
                    :alt="product.name"
                    class="product-thumb" />
                  <div class="product-cell-text">
                    <div
                      class="product-name d-flex align-center"
                      style="gap: 4px;">
                      {{ product.name }}
                      <v-icon
                        v-if="item.isError"
                        x-small
                        color="#a8332a">
                        mdi-alert-circle
                      </v-icon>
                    </div>
                    <div class="product-meta">
                      {{ item.code }} · {{ item.color }} · {{ item.size }}
                    </div>
                  </div>
                </div>
              </td>
              <!-- Qty -->
              <td class="col-qty text-right">
                <div class="qty-value">
                  <template v-if="item.originalCount !== undefined && item.originalCount !== item.count">
                    <span class="qty-strike">{{ item.originalCount }}</span>
                    {{ item.count }}
                  </template>
                  <template v-else>
                    {{ item.originalCount || item.count }}
                  </template>
                  <span class="qty-label">
                    ชิ้น
                  </span>
                </div>
                <div
                  v-if="item.stockQty !== undefined"
                  class="stock-badge"
                  :class="item.isError ? 'stock-badge--err' : ''">
                  คงคลัง {{ item.stockQty }}
                </div>
              </td>
              <!-- Allocation status -->
              <td class="col-alloc text-center">
                <div class="alloc-cell">
                  <v-progress-circular
                    :rotate="-90"
                    :size="22"
                    :width="2"
                    :value="item.count > 0 ? Math.min(calSkuTotal(item) / item.count * 100, 100) : 0"
                    :color="ringColor(item)"
                    class="alloc-ring" />
                  <div class="alloc-info">
                    <span class="alloc-count">
                      <span :class="allocClass(item)">{{ calSkuTotal(item) }}</span>
                      <span class="alloc-count-sep">/{{ item.count }}</span>
                    </span>
                    <span
                      class="alloc-status"
                      :class="allocClass(item)">
                      {{ allocStatusText(item) }}
                    </span>
                  </div>
                  <v-btn
                    icon
                    small
                    class="ml-1"
                    @click="(lockedRows[rowKey(pi, si)] || calSkuTotal(item) === item.count) ? toggleLock(rowKey(pi, si)) : autoSplitRow(pi, si)">
                    <v-icon
                      small
                      :color="lockedRows[rowKey(pi, si)] || calSkuTotal(item) === item.count ? '#2e7d4f' : '#214dff'">
                      {{ lockedRows[rowKey(pi, si)] ? 'mdi-lock' : (calSkuTotal(item) === item.count ? 'mdi-lock-open' : 'mdi-creation') }}
                    </v-icon>
                  </v-btn>
                </div>
              </td>
              <!-- Branch cells -->
              <td
                v-for="branch in selectedBranches"
                :key="`${item.code}-br-${branch.id}`"
                class="col-branch">
                <div class="branch-cell">
                  <v-text-field
                    v-model.number="getWarehouse(item, branch.id).amount"
                    outlined
                    dense
                    type="number"
                    min="0"
                    hide-details
                    :disabled="lockedRows[rowKey(pi, si)]"
                    class="branch-input"
                    @input="getWarehouse(item, branch.id).amount === '' ? getWarehouse(item, branch.id).amount = 0 : () => {}" />
                  <div class="branch-cell-pct">
                    <span :class="cellPctClass(item, branch)">{{ cellPct(item, branch) }}%</span>
                    <span
                      class="branch-cell-diff"
                      :class="diffClass(item, branch)">
                      {{ diffText(item, branch) }}
                    </span>
                  </div>
                </div>
              </td>
            </tr>
            <!-- Expanded detail row -->
            <tr
              v-if="expandedRows[rowKey(pi, si)]"
              class="expanded-row">
              <td
                :colspan="4 + selectedBranches.length"
                class="expanded-cell">
                <div class="expanded-inner">
                  <!-- SKU Detail -->
                  <div class="detail-section">
                    <div class="detail-title">
                      SKU Detail
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Code</span>
                      <span class="detail-value mono">{{ item.code }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Color</span>
                      <span class="detail-value">{{ item.color }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Size</span>
                      <span class="detail-value">{{ item.size }}</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Qty</span>
                      <span class="detail-value">{{ item.count }} ชิ้น</span>
                    </div>
                  </div>
                  <!-- Distribution Preview -->
                  <div class="detail-section flex-1">
                    <div class="detail-title">
                      Distribution Preview
                    </div>
                    <div
                      v-for="branch in selectedBranches"
                      :key="`prev-${branch.id}`"
                      class="preview-row">
                      <span
                        class="preview-label"
                        :title="branch.name">{{ branch.name }}</span>
                      <div class="preview-bar-track">
                        <div
                          class="preview-bar-fill"
                          :style="{ width: previewBarWidth(item, branch) + '%' }" />
                      </div>
                      <span class="preview-amount">{{ getWarehouse(item, branch.id).amount }}</span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Footer bar -->
    <div class="footer-bar elevation-3">
      <div
        class="d-flex align-center"
        style="gap: 10px; flex-wrap: wrap;">
        <v-progress-circular
          :rotate="-90"
          :size="20"
          :width="2"
          :value="totalQty > 0 ? Math.min(totalAllocated / totalQty * 100, 100) : 0"
          :color="totalQty > 0 && totalAllocated === totalQty ? '#2e7d4f' : '#b86c1d'"
          class="footer-ring" />
        <span class="footer-stat">
          <strong>{{ totalAllocated }}</strong> / {{ totalQty }} units allocated
        </span>
        <span class="footer-pipe">|</span>
        <span class="footer-stat">
          <strong>{{ completeSkuCount }}</strong> complete
        </span>
        <span
          class="footer-stat"
          :class="totalSkuRows - completeSkuCount - overSkuCount > 0 ? 'text-warn' : ''">
          <strong>{{ totalSkuRows - completeSkuCount - overSkuCount }}</strong> in progress
        </span>
        <span
          v-if="overSkuCount > 0"
          class="footer-stat text-err">
          <strong>{{ overSkuCount }}</strong> over
        </span>
        <span class="footer-stat">
          <strong>{{ lockedCount }}</strong> locked
        </span>
        <span
          v-if="insufficientSkuCount > 0"
          class="footer-stat text-err">
          <v-icon
            x-small
            color="#a8332a"
            class="mr-1">mdi-alert-circle</v-icon>
          <strong>{{ insufficientSkuCount }}</strong> insufficient stock
        </span>
      </div>
    </div>

    <v-overlay
      :value="loading"
      z-index="10">
      <gw-svg-icon
        class="cart-loading"
        svg-name="cart-loading"
        original />
    </v-overlay>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProductProvider from '@/resources/ProductProvider'
import AutoDeliveryNotesProvider from '@/resources/AutoDeliveryNotesProvider'
import { ExportToCsv } from 'export-to-csv'

const ProductService = new ProductProvider()
const AutoDeliveryNotesService = new AutoDeliveryNotesProvider()

export default {
  props: {
    note: {
      type: Object,
      default: () => {}
    },
    selectedBranches: {
      type: Array,
      default: () => []
    },
    warehouseId: {
      type: Number,
      default: null
    },
    total: {
      type: Object,
      default: () => {}
    },
    dueDate: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      products: [],
      loading: false,
      expandedRows: {},
      lockedRows: {},
      percentRules: [
        (v) => (v !== '' && v !== null && v !== undefined) || 'This field is required',
        (v) => +v >= 0 || 'Enter 0 or more',
        (v) => +v <= 100 || 'Enter 100 or less'
      ]
    }
  },
  computed: {
    ...mapGetters({
      warehouse: 'Store/warehouse'
    }),
    flatSkus () {
      return this.products.flatMap((product, pi) => product.skus.map((item, si) => ({ product, item, pi, si })))
    },
    totalPercent () {
      return this.selectedBranches.reduce((sum, branch) => sum + (+branch.percent), 0).toFixed(2)
    },
    totalQty () {
      return this.products.reduce((sum, p) => sum + p.skus.reduce((s, sku) => s + sku.count, 0), 0)
    },
    totalAllocated () {
      return this.products.reduce((sum, p) => sum + p.skus.reduce((s, sku) => s + this.calSkuTotal(sku), 0), 0)
    },
    completeSkuCount () {
      let n = 0
      this.products.forEach((p) => p.skus.forEach((sku) => { if (this.calSkuTotal(sku) === sku.count) n++ }))
      return n
    },
    overSkuCount () {
      let n = 0
      this.products.forEach((p) => p.skus.forEach((sku) => { if (this.calSkuTotal(sku) > sku.count) n++ }))
      return n
    },
    totalSkuRows () {
      return this.products.reduce((sum, p) => sum + p.skus.length, 0)
    },
    lockedCount () {
      return Object.values(this.lockedRows).filter(Boolean).length
    },
    insufficientSkuCount () {
      let n = 0
      this.products.forEach((p) => p.skus.forEach((sku) => { if (sku.isError) n++ }))
      return n
    },
    isGriNote () {
      return (this.note.code || '').slice(0, 3) === 'GRI'
    }
  },
  mounted () {
    this.initBranches()
    this.mappingSkus()
    this.checkStock()
  },
  methods: {
    calSkuTotal (sku) {
      const total = sku.warehouses.reduce((sum, w) => sum + w.amount, 0)
      return total
    },
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),
    rowKey (pi, si) {
      return `${pi}-${si}`
    },
    toggleExpand (key) {
      this.$set(this.expandedRows, key, !this.expandedRows[key])
    },
    toggleLock (key) {
      this.$set(this.lockedRows, key, !this.lockedRows[key])
    },
    autoSplitRow (pi, si) {
      const sku = this.products[pi].skus[si]
      const newWarehouses = this.distributeSkuToWarehouses(sku, this.selectedBranches)
      this.$set(this.products[pi].skus[si], 'warehouses', newWarehouses)
    },
    allocClass (item) {
      const total = this.calSkuTotal(item)
      if (total > item.count) return 'text-err'
      if (total === item.count) return 'text-ok'
      return 'text-warn'
    },
    allocStatusText (item) {
      const total = this.calSkuTotal(item)
      if (total > item.count) return `+${total - item.count} over`
      if (total === item.count) return 'OK · 100%'
      return `${item.count - total} left`
    },
    ringColor (item) {
      const total = this.calSkuTotal(item)
      if (total > item.count) return '#a8332a'
      if (total === item.count) return '#2e7d4f'
      return '#b86c1d'
    },
    getWarehouse (item, branchId) {
      const found = item.warehouses.find((w) => w.id === branchId)
      if (found) return found
      return { amount: 0 }
    },
    cellPct (item, branch) {
      if (!item.count) return '0.0'
      const wh = this.getWarehouse(item, branch.id)
      return ((wh.amount / item.count) * 100).toFixed(1)
    },
    cellPctClass (item, branch) {
      const pct = parseFloat(this.cellPct(item, branch))
      const target = parseFloat(branch.percent)
      if (pct > target) return 'text-err'
      if (pct === target) return 'text-ok'
      return 'text-warn'
    },
    diffText (item, branch) {
      const wh = this.getWarehouse(item, branch.id)
      const expected = Math.round(item.count * (branch.percent / 100))
      const diff = wh.amount - expected
      if (diff === 0) return ''
      return diff > 0 ? `+${diff}` : `${diff}`
    },
    diffClass (item, branch) {
      const wh = this.getWarehouse(item, branch.id)
      const expected = Math.round(item.count * (branch.percent / 100))
      const diff = wh.amount - expected
      if (diff > 0) return 'text-err'
      if (diff < 0) return 'text-warn'
      return ''
    },
    previewBarWidth (item, branch) {
      if (!item.count) return 0
      const maxAlloc = Math.max(
        ...this.selectedBranches.map((b) => this.getWarehouse(item, b.id).amount)
      )
      if (!maxAlloc) return 0
      const wh = this.getWarehouse(item, branch.id)
      return Math.min((wh.amount / maxAlloc) * 100, 100)
    },
    initBranches () {
      this.selectedBranches.forEach((branch) => {
        const tmpBranch = branch
        tmpBranch.isCeil = false
      })
    },
    mappingSkus (reSeparate = false) {
      if (!reSeparate) {
        this.products = this.separateProducts(this.note.products)
      } else {
        this.products = this.separateProducts(this.products, reSeparate)
      }
    },
    distributeSkuToWarehouses (sku, branchPayload) {
      const sortedBranches = [...branchPayload].sort((a, b) => a.percent - b.percent)
      let assignedAmount = 0

      const warehouses = sortedBranches.map((branch) => {
        const remainAmount = Math.max(sku.count - assignedAmount, 0)
        const warehouse = JSON.parse(JSON.stringify(branch))
        const rawAmount = sku.count * (branch.percent / 100)
        const separateAmount = (rawAmount % 1) > 0.7 ? Math.ceil(rawAmount) : Math.floor(rawAmount)
        if (remainAmount > 0 && separateAmount <= remainAmount) {
          warehouse.amount = separateAmount
        } else {
          warehouse.amount = remainAmount
        }

        assignedAmount += warehouse.amount
        return warehouse
      })

      let remainder = sku.count - assignedAmount
      let i = warehouses.length - 1
      while (remainder > 0) {
        warehouses[i].amount += 1
        i = i - 1 < 0 ? warehouses.length - 1 : i - 1
        remainder--
      }

      return warehouses
    },
    separateProducts (products, reSeparate = false) {
      return products.map((product, pi) => ({
        ...product,
        skus: product.skus.map((sku, si) => {
          const isLock = this.lockedRows[this.rowKey(pi, si)]
          if (isLock && reSeparate) return sku
          const tempSku = JSON.parse(JSON.stringify(sku))
          tempSku.isError = tempSku.isError ?? false
          tempSku.warehouses = this.distributeSkuToWarehouses(tempSku, this.selectedBranches)
          return tempSku
        })
      }))
    },
    async checkStock () {
      try {
        this.loading = true

        const productIds = this.products.map((product) => product.id)
        let hasError = false
        let hasClamped = false
        const { data: fetchProducts } = await ProductService.getManyProductById({ productIds })
        this.products = this.products.map((pd) => {
          const tempPd = pd
          const matchProduct = fetchProducts.find((p) => p.id === pd.id)
          tempPd.skus = tempPd.skus.map((sku) => {
            const tempSku = JSON.parse(JSON.stringify(sku))
            const matchSku = matchProduct.skus.find((sk) => sk.id === tempSku.id)
            const stock = matchSku.stock.find((w) => w.warehouse.id === this.warehouseId)
            const stockQty = stock.onHandQty - stock.onReservedQty
            tempSku.stockQty = stockQty
            if (this.isGriNote && stockQty > 0 && stockQty < tempSku.count) {
              tempSku.originalCount = tempSku.count
              tempSku.count = stockQty
              hasClamped = true
            }

            return tempSku
          })
          return tempPd
        })

        if (hasClamped) this.mappingSkus(true)

        this.products.forEach((pd) => pd.skus.forEach((sku) => {
          const tempSku = sku
          tempSku.isError = this.calSkuTotal(tempSku) > tempSku.stockQty
          if (tempSku.isError) hasError = true
        }))

        if (hasError) {
          const error = { code: 400, message: 'Amount product is not enough.' }
          throw error
        }

        return true
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code} : ${error.message}`,
          type: 'error'
        })

        return false
      } finally {
        this.loading = false
      }
    },
    confirmMoveProducts () {
      this.setModal({
        value: true,
        title: 'Confirmation',
        message: 'Are you sure to do this action',
        confirmText: 'Confirm',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.moveProducts()
      })
    },
    async moveProducts () {
      const isEnough = await this.checkStock()

      if (isEnough) {
        this.createNote()
      }
    },
    findWarehouse (id) {
      const w = this.warehouse.find((r) => r.id === id)
      return {
        id: w.id,
        name: w.name,
        code: w.code
      }
    },
    optimizePayload () {
      const tmpProducts = JSON.parse(JSON.stringify(this.products))
      const cleanProducts = tmpProducts
        .map((product) => ({
          id: product.id,
          name: product.name,
          model: product.model,
          photoUrl: product.photoUrl,
          skus: product.skus.filter((sku) => this.calSkuTotal(sku) > 0)
        }))
        .filter((product) => product.skus.length > 0)

      if (!cleanProducts.length) {
        this.setSnackbar({
          value: true,
          message: 'Not enough products to create.',
          type: 'error'
        })

        return []
      }

      let warehouses = this.selectedBranches.map((w) => {
        const payload = {
          warehouse: {
            id: w.id,
            name: w.name,
            code: w.code
          },
          products: []
        }

        for (const product of cleanProducts) {
          const tmpProduct = {
            ...product,
            skus: []
          }

          for (const sku of product.skus) {
            const found = sku.warehouses.find((wh) => wh.id === w.id)

            if (found && found?.amount > 0) {
              tmpProduct.skus.push({
                id: sku.id,
                code: sku.code,
                price: sku.price,
                option: sku.option,
                color: sku.color,
                size: sku.size,
                specialPrice: sku.specialPrice,
                amount: found.amount,
                count: 0
              })
            }
          }

          if (tmpProduct.skus.length) {
            payload.products.push(tmpProduct)
          }
        }

        return payload
      })

      warehouses = warehouses.filter((w) => w.products.length > 0)

      return warehouses
    },
    async createNote () {
      try {
        this.loading = true

        const { data } = await AutoDeliveryNotesService.createAutoDeliveryNote({
          deliveryNoteId: this.note.id,
          totalProduct: this.total.product,
          totalAmount: this.total.amount,
          dueDate: this.dueDate,
          fromWarehouse: this.findWarehouse(this.warehouseId),
          toWarehouses: this.optimizePayload()
        })

        this.setSnackbar({
          value: true,
          message: 'Create delivery and withdraws notes success.',
          type: 'success'
        })

        this.$emit('summary', [data])
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error code ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    exportDataToCSV () {
      const items = []
      const products = JSON.parse(JSON.stringify(this.products))
      products.forEach((product) => {
        product.skus.forEach((sku) => {
          const tmpSku = {
            name: product.name,
            code: sku.code,
            size: sku.size,
            color: sku.color,
            total: this.calSkuTotal(sku),
            count: sku.count
          }

          sku.warehouses.forEach((wh) => {
            tmpSku[`${wh.name}`] = wh.amount
          })

          items.push(tmpSku)
        })
      })
      const options = {
        filename: `Stock_${this.note.code}`,
        showLabels: true,
        useKeysAsHeaders: true
      }

      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(items)
    }
  }
}
</script>

<style scoped>
/* ── Design tokens ──────────────────────────────── */
:root {
  --bg: #ffffff;
  --surface: #ffffff;
  --surface2: #f7f6f2;
  --ink: #1a1a1a;
  --ink2: #4a4a4a;
  --ink3: #8a8580;
  --hairline: #e8e5dd;
  --hairline2: #d8d4ca;
  --ok: #2e7d4f;
  --ok-bg: #e8f1ea;
  --warn: #b86c1d;
  --warn-bg: #fbf1e3;
  --err: #a8332a;
  --err-bg: #f8e9e7;
}

/* ── Layout shell ───────────────────────────────── */
.separate-products-container {
  /* height: calc(100vh - 68px - 44px); */
  height: fit-content;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 0.625rem;
}

/* ── Delivery strip ─────────────────────────────── */
.delivery-strip {
  background: #ffffff;
  border-bottom: 1px solid #e8e5dd;
  display: flex;
  align-items: center;
  padding: 10px 20px;
  flex-shrink: 0;
  gap: 0;
  min-height: 64px;
}
.strip-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0 20px;
}
.strip-block--note {
  padding-left: 0;
}
.strip-block-label {
  font-size: 10px;
  font-weight: 600;
  color: #8a8580;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: nowrap;
}
.strip-block-value {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
}
.strip-block-unit {
  font-size: 12px;
  font-weight: 400;
  color: #8a8580;
}
.strip-divider {
  width: 1px;
  height: 36px;
  background: #e8e5dd;
  flex-shrink: 0;
}
.strip-code {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}
.strip-status {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #2e7d4f;
  background: #e8f1ea;
  border-radius: 20px;
  padding: 2px 8px;
  text-transform: uppercase;
}
.strip-btn {
  background: transparent !important;
  color: #4a4a4a !important;
  text-transform: none !important;
  font-size: 12px !important;
  letter-spacing: 0 !important;
  border: 1px solid #d8d4ca !important;
  border-radius: 0.275rem !important;
}
.strip-btn--primary {
  background: #1a1a1a !important;
  color: #ffffff !important;
  border-color: #1a1a1a !important;
}

/* ── Table wrapper ──────────────────────────────── */
.table-outer {
  overflow-x: auto;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

/* ── Table base ─────────────────────────────────── */
.allocation-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
}

/* ── Sticky header ──────────────────────────────── */
.allocation-table thead tr th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #ffffff;
  border-bottom: 2px solid #e8e5dd;
  padding: 10px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #4a4a4a;
  white-space: nowrap;
}

/* ── Sticky columns ─────────────────────────────── */
.sticky-left-0 {
  position: sticky;
  left: 0;
  z-index: 2;
  background: inherit;
}
.sticky-left-36 {
  position: sticky;
  left: 36px;
  z-index: 2;
  background: inherit;
}

/* thead sticky cols need higher z-index */
.allocation-table thead tr th.sticky-left-0 {
  z-index: 5;
  background: #ffffff;
}
.allocation-table thead tr th.sticky-left-36 {
  z-index: 5;
  background: #ffffff;
}

/* ── Column widths ──────────────────────────────── */
.col-expand {
  width: 36px;
  min-width: 36px;
  max-width: 36px;
}
.col-product {
  width: 310px;
  min-width: 310px;
}
.col-qty {
  width: 120px;
  min-width: 120px;
}
.col-alloc {
  width: 160px;
  min-width: 160px;
}
.col-branch {
  width: 180px;
  min-width: 180px;
}

/* ── Col label helpers ──────────────────────────── */
.col-label-th {
  font-weight: 700;
  color: #1a1a1a;
}
.col-label-en {
  font-weight: 400;
  color: #8a8580;
}

/* ── Branch header ──────────────────────────────── */
.branch-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 2px 0;
}
.branch-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #1a1a1a;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.branch-pct-row {
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.branch-pct-field {
  width: 40px;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  border: none;
  outline: none;
  background: transparent;
  text-align: left;
  padding: 0;
  -moz-appearance: textfield;
  appearance: textfield;
}
.branch-pct-field::-webkit-inner-spin-button,
.branch-pct-field::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.branch-pct-field:focus {
  border-bottom: 1.5px solid #d8d4ca;
}
.branch-pct-unit {
  font-size: 11px;
  font-weight: 500;
  color: #8a8580;
}

/* ── SKU row ────────────────────────────────────── */
.sku-row {
  background: #ffffff;
}
.sku-row:hover {
  background: #ffffff;
}
.row-locked {
  background: #f7f6f2;
  opacity: 0.85;
}
.allocation-table tbody tr td {
  border-bottom: 1px solid #e8e5dd;
  padding: 6px 10px;
  font-size: 13px;
  color: #1a1a1a;
  vertical-align: middle;
}

/* sticky td background */
.sku-row td.sticky-left-0,
.sku-row td.sticky-left-36 {
  background: #ffffff;
}
.sku-row:hover td.sticky-left-0,
.sku-row:hover td.sticky-left-36 {
  background: #ffffff;
}
.row-locked td.sticky-left-0,
.row-locked td.sticky-left-36 {
  background: #f7f6f2;
}

/* ── Product cell layout ────────────────────────── */
.product-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.product-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 3px;
  background: #f7f6f2;
  flex-shrink: 0;
}
.product-cell-text {
  flex: 1;
  min-width: 0;
}

/* ── Product info ───────────────────────────────── */
.product-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}
.product-meta {
  font-size: 11px;
  color: #8a8580;
  font-family: monospace;
  margin-top: 2px;
}

/* ── Qty ────────────────────────────────────────── */
.qty-value {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}
.qty-label {
  font-size: 10px;
  color: #8a8580;
}
.qty-strike {
  text-decoration: line-through;
  color: #8a8580;
  font-weight: 400;
  margin-right: 4px;
}
.stock-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #4a4a4a;
  /* background: #f7f6f2;
  border: 1px solid #e8e5dd;
  border-radius: 4px;
  padding: 1px 6px;
  margin-top: 3px; */
}
.stock-badge--err {
  color: #a8332a;
  /* background: #f8e9e7;
  border-color: #f0c8c5; */
  font-weight: 600;
}

/* ── Allocation cell ────────────────────────────── */
.alloc-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}
.alloc-ring {
  flex-shrink: 0;
}
.alloc-ring >>> .v-progress-circular__underlay {
  stroke: #e8e5dd !important;
  stroke-opacity: 1 !important;
}
.footer-ring >>> .v-progress-circular__underlay {
  stroke: #e8e5dd !important;
  stroke-opacity: 1 !important;
}
.alloc-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.alloc-count {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
}
.alloc-count-sep {
  font-size: 11px;
  font-weight: 400;
  color: #8a8580;
}
.alloc-status {
  font-size: 10px;
  font-weight: 500;
}

/* ── Branch cell ────────────────────────────────── */
.branch-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.branch-input {
  font-size: 13px;
}
.branch-cell-pct {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
}
.branch-cell-diff {
  font-size: 10px;
}

/* ── Expanded row ───────────────────────────────── */
.expanded-row {
  background: #ffffff;
}
.expanded-cell {
  padding: 16px 20px !important;
  border-bottom: 2px solid #d8d4ca !important;
  position: sticky;
  left: 0;
  box-sizing: border-box;
}
.expanded-inner {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  max-width: 900px;
}
.detail-section {
  min-width: 180px;
}
.flex-1 {
  flex: 1;
}
.detail-title {
  font-size: 11px;
  font-weight: 700;
  color: #8a8580;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}
.detail-row {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  align-items: center;
}
.detail-label {
  font-size: 12px;
  color: #8a8580;
  width: 48px;
  flex-shrink: 0;
}
.detail-value {
  font-size: 12px;
  color: #1a1a1a;
  font-weight: 500;
}
.mono {
  font-family: monospace;
}

/* ── Preview bars ───────────────────────────────── */
.preview-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}
.preview-label {
  width: 130px;
  font-size: 11px;
  color: #4a4a4a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}
.preview-bar-track {
  flex: 1;
  height: 8px;
  background: #e8e5dd;
  border-radius: 4px;
  overflow: hidden;
}
.preview-bar-fill {
  height: 100%;
  background: #2e7d4f;
  border-radius: 4px;
  transition: width 0.2s ease;
}
.preview-amount {
  font-size: 11px;
  color: #4a4a4a;
  width: 28px;
  text-align: right;
  flex-shrink: 0;
}

/* ── Footer bar ─────────────────────────────────── */
.footer-bar {
  position: sticky;
  bottom: 1rem;
  left: 0;
  margin-top: 1.25rem;
  width: 100%;
  z-index: 6;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.footer-stat {
  font-size: 13px;
}
.footer-pipe {
  color: #d0ccc4;
  margin: 0 6px;
  font-weight: 300;
  font-size: 14px;
}

/* ── Semantic color helpers ─────────────────────── */
.text-ok {
  color: #2e7d4f;
}
.text-warn {
  color: #b86c1d;
}
.text-err {
  color: #a8332a;
}
</style>
